import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy, increment, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Search, Store, Filter } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

const LocalesPage = () => {
  const [locales, setLocales] = useState([]);
  const [filteredLocales, setFilteredLocales] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocales();
  }, []);

  useEffect(() => {
    filterLocales();
  }, [searchTerm, selectedCategory, locales]);

  const fetchLocales = async () => {
    try {
      const localesQuery = query(
        collection(db, 'locales'),
        orderBy('nombre', 'asc')
      );
      const localesSnapshot = await getDocs(localesQuery);
      const localesData = localesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setLocales(localesData);
      setFilteredLocales(localesData);
    } catch (error) {
      console.error('Error fetching locales:', error);
      toast.error('Error al cargar los locales');
    } finally {
      setLoading(false);
    }
  };

  const filterLocales = () => {
    let filtered = locales;

    if (searchTerm) {
      filtered = filtered.filter(local =>
        local.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        local.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (local.keywords && local.keywords.some(keyword => 
          keyword.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(local => local.categoria === selectedCategory);
    }

    setFilteredLocales(filtered);
  };

  const categories = ['all', ...new Set(locales.map(local => local.categoria))];

  const handleLocalClick = async (localId) => {
    try {
      const localRef = doc(db, 'locales', localId);
      await updateDoc(localRef, {
        visitas: increment(1)
      });
    } catch (error) {
      console.error('Error updating visits:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Directorio de <span className="gradient-text">Locales</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explora todos nuestros locales comerciales y encuentra lo que buscas
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12 flex flex-col md:flex-row gap-4 items-center"
          >
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <Input
                type="text"
                placeholder="Buscar por nombre o palabra clave..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Filter className="text-muted-foreground" size={20} />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-[200px] h-12">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.filter(cat => cat !== 'all').map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          {filteredLocales.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Store className="mx-auto mb-4 text-muted-foreground" size={64} />
              <h3 className="text-2xl font-heading font-semibold mb-2">No se encontraron locales</h3>
              <p className="text-muted-foreground">Intenta con otra búsqueda o filtro</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredLocales.map((local, index) => (
                <motion.div
                  key={local.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <Link to={`/local/${local.slug}`} onClick={() => handleLocalClick(local.id)}>
                    <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={local.imagenPrincipal}
                          alt={local.nombre}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4">
                          <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                            {local.categoria}
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-6 flex-grow flex flex-col">
                        <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                          {local.nombre}
                        </h3>
                        <p className="text-muted-foreground mb-4 flex-grow">
                          {local.descripcion}
                        </p>
                        <Button variant="ghost" className="w-full mt-auto justify-between group-hover:text-primary">
                          Ver más detalles
                          <Store size={18} />
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LocalesPage;